/** Local play record, kept on the device. */
import type { GameState } from '@horde/engine';

const KEY = 'horde:stats:v1';

export interface Stats {
  played: number;
  wins: number;
  domination: number;
  eradication: number;
  fallen: number;
  bestRound: number;
  longestGame: number;
  lastPlayedAt: number | null;
}

const empty: Stats = {
  played: 0,
  wins: 0,
  domination: 0,
  eradication: 0,
  fallen: 0,
  bestRound: 0,
  longestGame: 0,
  lastPlayedAt: null,
};

export function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...empty, ...(JSON.parse(raw) as Partial<Stats>) } : { ...empty };
  } catch {
    return { ...empty };
  }
}

function save(s: Stats): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

/** Record a finished game from `me`'s point of view. Idempotent per game via the caller. */
export function recordOutcome(state: GameState, me: string | null): Stats {
  const s = loadStats();
  const o = state.outcome;
  if (!o) return s;
  s.played += 1;
  s.longestGame = Math.max(s.longestGame, state.round);
  s.lastPlayedAt = Date.now();
  if (o.kind === 'victory' && o.winner === me) {
    s.wins += 1;
    if (o.by === 'domination') s.domination += 1;
    else s.eradication += 1;
    s.bestRound = Math.max(s.bestRound, state.round);
  } else {
    s.fallen += 1;
    // Survived until the end even if someone else won or the horde did.
    const mine = me ? state.players.find((p) => p.id === me) : null;
    if (mine && !mine.eliminated) s.bestRound = Math.max(s.bestRound, state.round);
  }
  save(s);
  return s;
}
